import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const LONG_WRAPPER_MIN_HEIGHT = 0.6;
const LONG_WRAPPER_MIN_ELEMENTS = 2;
const HEADING_REVEAL_SCRUB = 0.7;
const PARAGRAPH_REVEAL_SCRUB = 0.8;
const LONG_WRAPPER_SCRUB = 0.9;
const IMAGE_REVEAL_SCRUB = 0.8;
const EARLY_REVEAL_CLASS = "cw-text-reveal--early";
const EARLY_HEADING_START = "top 92%";
const EARLY_HEADING_END = "top 68%";
const EARLY_PARAGRAPH_START = "top 88%";
const EARLY_PARAGRAPH_END = "top 62%";

export const textReveal = () => {
	const isWordPressEditor =
		document.body?.classList.contains("wp-admin") ||
		document.body?.classList.contains("block-editor-iframe__body") ||
		document.documentElement.classList.contains(
			"block-editor-iframe__html",
		);

	if (isWordPressEditor) return;

	const wrappers = document.querySelectorAll(".cw-text-reveal");

	if (!wrappers.length) return;

	wrappers.forEach((wrapper) => {
		const isTestimonialText =
			wrapper.matches("p") && wrapper.closest(".testimonials");

		const elements = isTestimonialText
			? [wrapper]
			: wrapper.querySelectorAll("h1, h2, h3, h4, h5, h6, p");

		elements.forEach((element) => {
			const charSelector = isTestimonialText
				? ".cw-testimonial-char"
				: ".char";

			if (!element.querySelector(charSelector)) {
				splitChars(element, isTestimonialText);
			}
		});
	});
	const mm = gsap.matchMedia();

	mm.add("(min-width: 992px)", () => {
		createAnimations(getScroller());
	});

	mm.add("(max-width: 991px)", () => {
		createAnimations(getScroller());
	});
};

function getScroller() {
	const isHome = document.body.classList.contains("home");
	const isDesktop = window.innerWidth >= 992;

	return isHome && isDesktop ? ".wp-site-blocks" : undefined;
}

function createAnimations(scroller) {
	const wrappers = document.querySelectorAll(".cw-text-reveal");

	wrappers.forEach((wrapper) => {
		if (wrapper.classList.contains("tab-features__text-reveal")) return;

		const headings = wrapper.querySelectorAll("h1, h2, h3, h4, h5, h6");

		headings.forEach((title) => {
			const chars = title.querySelectorAll(".char");

			gsap.fromTo(
				chars,
				{
					opacity: 0,
				},
				{
					opacity: 1,
					stagger: 0.015,
					ease: "none",
					scrollTrigger: {
						trigger: title,
						scroller,
						start: wrapper.classList.contains(EARLY_REVEAL_CLASS)
							? EARLY_HEADING_START
							: "top 80%",
						end: wrapper.classList.contains(EARLY_REVEAL_CLASS)
							? EARLY_HEADING_END
							: "top 50%",
						scrub: HEADING_REVEAL_SCRUB,
					},
				},
			);
		});

		const isTestimonialText =
			wrapper.matches("p") && wrapper.closest(".testimonials");

		const paragraphs = isTestimonialText
			? [wrapper]
			: wrapper.querySelectorAll("p");

		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: wrapper,
				scroller,
				start: wrapper.classList.contains(EARLY_REVEAL_CLASS)
					? EARLY_PARAGRAPH_START
					: "top 70%",
				end: getParagraphRevealEnd(
					wrapper,
					paragraphs,
					isTestimonialText,
				),
				scrub: getParagraphRevealScrub(
					wrapper,
					paragraphs,
					isTestimonialText,
				),
			},
		});

		paragraphs.forEach((p) => {
			const chars = isTestimonialText
				? p.querySelectorAll(".cw-testimonial-char")
				: p.querySelectorAll(".char");

			if (!chars.length) return;

			tl.fromTo(
				chars,
				{
					opacity: 0,
				},
				{
					opacity: 1,
					stagger: 0.015,
					ease: "none",
					duration: 0.8,
				},
				">0.08",
			);

			const link = p.classList.contains("custom-arrow-link") ? p : null;

			if (link) {
				ScrollTrigger.create({
					trigger: p,
					scroller,
					start: "top 60%",
					onEnter: () => link.classList.add("is-visible"),
					onLeaveBack: () => link.classList.remove("is-visible"),
				});
			}
		});
	});

	ScrollTrigger.refresh();
}

function getParagraphRevealEnd(wrapper, paragraphs, isTestimonialText) {
	if (wrapper.classList.contains(EARLY_REVEAL_CLASS)) {
		return EARLY_PARAGRAPH_END;
	}

	if (isTestimonialText) {
		return "top 40%";
	}

	if (isLongRevealWrapper(wrapper, paragraphs)) {
		return "bottom 72%";
	}

	return "top 40%";
}

function getParagraphRevealScrub(wrapper, paragraphs, isTestimonialText) {
	if (isTestimonialText) {
		return PARAGRAPH_REVEAL_SCRUB;
	}

	return isLongRevealWrapper(wrapper, paragraphs)
		? LONG_WRAPPER_SCRUB
		: PARAGRAPH_REVEAL_SCRUB;
}

function isLongRevealWrapper(wrapper, paragraphs) {
	return (
		paragraphs.length > LONG_WRAPPER_MIN_ELEMENTS ||
		wrapper.offsetHeight > window.innerHeight * LONG_WRAPPER_MIN_HEIGHT
	);
}

function splitChars(element, isTestimonialText = false) {
	const nodes = [...element.childNodes];
	element.innerHTML = "";

	nodes.forEach((node) => {
		if (node.nodeType === Node.TEXT_NODE) {
			const text = node.textContent;
			const words = text.split(/(\s+)/);

			words.forEach((part) => {
				if (part === "") return;

				if (/\s+/.test(part)) {
					element.appendChild(document.createTextNode(part));
					return;
				}

				const wordSpan = document.createElement("span");

				wordSpan.className = isTestimonialText
					? "cw-testimonial-word"
					: "word";

				[...part].forEach((char) => {
					const charSpan = document.createElement("span");

					charSpan.className = isTestimonialText
						? "cw-testimonial-char"
						: "char";

					charSpan.textContent = char;
					wordSpan.appendChild(charSpan);
				});

				element.appendChild(wordSpan);
			});

			return;
		}

		if (node.nodeType === Node.ELEMENT_NODE) {
			const clone = node.cloneNode(true);

			if (isTestimonialText && clone.tagName === "BR") {
				element.appendChild(clone);
				return;
			}

			splitChars(clone, isTestimonialText);
			element.appendChild(clone);
		}
	});
}

export const imageReveal = () => {
	const images = document.querySelectorAll(".cw-image-reveal");

	if (!images.length) return;

	images.forEach((image) => {
		const img = image.querySelector("img");

		if (!img) return;

		gsap.fromTo(
			img,
			{
				clipPath: "inset(0% 0% 100% 0%)",
				opacity: 0.2,
			},
			{
				clipPath: "inset(0% 0% 0% 0%)",
				opacity: 1,
				ease: "none",
				scrollTrigger: {
					trigger: image.closest(".block-0__texto-foto") || image,
					scroller: getScroller(),
					start: "top 55%",
					end: "top 20%",
					scrub: IMAGE_REVEAL_SCRUB,
				},
			},
		);
	});
};
